import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslateText } from "../api/hooks";
import { detectIncomingTranslateTarget } from "../utils/detect-incoming-language";
import { TRANSLATE_TEXT_MAX_LENGTH } from "../types";
import type { TranslationLanguage } from "../types";

// Read-only translate for an incoming message bubble. Direction is decided
// locally per message via a language detector, not the LLM; targetLanguage
// stays null (button hidden by the caller) when the detector isn't confident.
// `enabled` should be false for outbound messages — they never show the
// toggle, so skip the detector's synchronous eld.detect() call entirely.
// `subjectText` is email-only: when set, translate() also translates it.
export const useMessageTranslation = (
  originalText: string,
  enabled = true,
  subjectText?: string
) => {
  const { mutate, isPending: isTranslating } = useTranslateText();
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const [translatedSubject, setTranslatedSubject] = useState<string | null>(
    null
  );
  const [showingTranslation, setShowingTranslation] = useState(false);
  const [targetLanguage, setTargetLanguage] =
    useState<TranslationLanguage | null>(null);
  // Guards against a stale onSuccess if this hook instance gets reused for
  // a different message (list virtualization).
  const generationRef = useRef(0);

  useEffect(() => {
    generationRef.current += 1;
    setTranslatedText(null);
    setTranslatedSubject(null);
    setShowingTranslation(false);
  }, [originalText, subjectText]);

  useEffect(() => {
    if (!enabled) {
      setTargetLanguage(null);
      return;
    }
    let cancelled = false;
    setTargetLanguage(null);
    detectIncomingTranslateTarget(originalText)
      .then((result) => {
        if (!cancelled) setTargetLanguage(result);
      })
      .catch((error: unknown) => {
        // Detector failed to load/run — hide the button instead of hanging.
        if (!cancelled) setTargetLanguage(null);
        // eslint-disable-next-line no-console
        console.error("Incoming-message language detection failed:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [originalText, enabled]);

  const translate = useCallback(() => {
    if (translatedText !== null) {
      setShowingTranslation(true);
      return;
    }
    const trimmedLength = originalText.trim().length;
    if (!trimmedLength || !targetLanguage) return;
    if (trimmedLength > TRANSLATE_TEXT_MAX_LENGTH) {
      toast.error(
        `Message is too long to translate (max ${TRANSLATE_TEXT_MAX_LENGTH.toLocaleString()} characters).`
      );
      return;
    }
    const generation = generationRef.current;

    mutate(
      {
        text: originalText,
        targetLanguage,
        ...(subjectText?.trim() && { subject: subjectText })
      },
      {
        onSuccess: ({ translatedText: result, translatedSubject: subject }) => {
          if (generation !== generationRef.current) return;
          setTranslatedText(result);
          if (typeof subject === "string") setTranslatedSubject(subject);
          setShowingTranslation(true);
        },
        onError: () => {
          if (generation !== generationRef.current) return;
          toast.error("We couldn't translate your message. Try again.");
        }
      }
    );
  }, [originalText, subjectText, targetLanguage, translatedText, mutate]);

  const showOriginal = useCallback(() => setShowingTranslation(false), []);

  return {
    translate,
    showOriginal,
    showingTranslation,
    translatedText,
    translatedSubject,
    targetLanguage,
    isTranslating
  };
};
